// Desktop Conversation detail page (per-recording view).
//
// Route: /conversations/:conversationId
//
// Renders metadata + transcript for a single recording, in the desktop chrome.
// Linked from `ThreadDetailPage`'s recording rows. This page intentionally
// replaces the misdirected `/analysis/:conversation_id` link that used to
// load the legacy per-customer renderers (Demo / UNTUCKit / Arcteryx) —
// none of which are correct for Toyota / Gee data. The old `/analysis`
// route is left intact for the legacy admin/demo flows.
//
// Layout:
//   - Header: "Recording" title + breadcrumb back to parent thread.
//   - Section A: Metadata panel (id, timestamps, recording / pipeline state).
//   - Section B: Transcript tabs — Cleaned (default) / Tagged / Raw.
//
// Transcript rendering tolerates both pre-formatted multi-speaker text
// (TAGGED, CLEANED) and a JSON-encoded array of word objects (RAW), which
// is what the live tagger emits. The RAW branch groups consecutive words
// by `speaker_id` into turn blocks. See `EmbedTranscriptView` for the
// speaker-bubble visual pattern this page ports to desktop chrome.

import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Heading,
  Spinner,
  Button,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Avatar,
} from "@chakra-ui/react";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import PageLayoutWithHeaderNav from "./PageLayoutWithHeaderNav";
import ConversationLifecycleBar from "./ConversationLifecycleBar";
import BatchProgressTable from "./BatchProgressTable";
import ConversationAudioPlayer from "./ConversationAudioPlayer";
import TaggedTranscriptView from "./TaggedTranscriptView";
import CleanedTranscriptView from "./CleanedTranscriptView";
import RawTranscriptView, {
  collectSnippetBoundaries,
} from "./RawTranscriptView";
import {
  getConversation,
  getConversationDetailItems,
  listAnalysisRuns,
  buildThreadEventsUrl,
} from "../services/embed";
import { currentEmbedToken } from "../EmbedAuthProvider";
import { formatDate } from "../utils/dateUtils";

// SSE events that move the batch table forward AND/OR change the
// lifecycle bar's analysis state. Listed in one place so the
// EventSource useEffect wiring stays in sync with BatchProgressTable's
// reducer.
const CONVERSATION_DETAIL_SSE_EVENTS = [
  "batch_transcription_started",
  "batch_transcription_complete",
  "raw_transcript_emitted",
  "batch_tagged_started",
  "batch_tagged_failed",
  "tagged_transcript_ready",
  "batch_cleaned_started",
  "batch_cleaned_failed",
  "cleaned_transcript_ready",
  "recording_state_changed",
  "analysis_started",
  "analysis_starting",
  "analysis_progress",
  "analysis_complete",
  "analysis_failed",
];

const defaultEventSourceFactory = (url) =>
  typeof window !== "undefined" && window.EventSource
    ? new window.EventSource(url)
    : null;

// --- Speaker styling shared across all three tabs ----------------------------

const SPEAKER_COLORS = ["blue", "purple", "green", "orange", "teal", "pink"];

function colorForSpeaker(speakerKey, registry) {
  if (!speakerKey) return "gray";
  if (registry.has(speakerKey)) return registry.get(speakerKey);
  const idx = registry.size % SPEAKER_COLORS.length;
  const color = SPEAKER_COLORS[idx];
  registry.set(speakerKey, color);
  return color;
}

function speakerLabel(rawSpeaker) {
  if (!rawSpeaker) return "Speaker";
  const s = String(rawSpeaker).trim();
  if (/^customer/i.test(s)) return "Customer";
  if (/^employee|^agent|^you|^self|^salesperson|^advisor/i.test(s)) {
    return "Employee";
  }
  // SPEAKER_00 / SPEAKER_01 style — collapse to "Speaker 0" / "Speaker 1"
  const m = s.match(/^SPEAKER[_-]?0*(\d+)$/i);
  if (m) return `Speaker ${m[1]}`;
  return s;
}

// --- Transcript turn block (matches EmbedTranscriptView bubble) --------------

function TurnBlock({ speaker, text, registry }) {
  const label = speakerLabel(speaker);
  const color = colorForSpeaker(speaker || label, registry);
  return (
    <Flex
      gap={3}
      align="flex-start"
      data-testid="transcript-turn"
      data-speaker={label}
    >
      <Avatar
        name={label}
        size="sm"
        bg={`${color}.500`}
        color="white"
        flexShrink={0}
      />
      <Box flex={1} minW={0}>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color={`${color}.600`}
          mb={1}
          textTransform="uppercase"
          letterSpacing="wide"
        >
          {label}
        </Text>
        <Box
          bg="gray.50"
          borderRadius="lg"
          borderLeftWidth="3px"
          borderLeftColor={`${color}.300`}
          px={4}
          py={2}
        >
          <Text fontSize="sm" lineHeight="1.6" whiteSpace="pre-wrap">
            {text}
          </Text>
        </Box>
      </Box>
    </Flex>
  );
}

// --- Per-shape renderers -----------------------------------------------------

// CLEANED / TAGGED text shape: a string with ONE TURN PER LINE, each line
// starting with `Speaker (Role): "utterance"`. Examples emitted by the
// tagger today: `Customer (C): "..."`, `Employee (SP): "..."`, `Other: ...`.
// Multi-line utterances continue on subsequent lines without a label until
// the next labeled line.
//
// Walks line-by-line, detects speaker-labeled lines, accumulates content
// until the next labeled line. Earlier implementation split on `\n{2,}`
// (blank-line-separated blocks) which does NOT match the tagger's actual
// output — every turn collapsed into a single bubble. See the screenshot
// in conv 334 / thread 166 for the failure mode.
const SPEAKER_LABEL_RE = /^\s*([A-Za-z][A-Za-z ]*?(?:\s*\([A-Za-z]+\))?)\s*:\s*"?(.*?)\s*$/;

function renderFormattedText(text, registry) {
  const lines = String(text).split(/\r?\n/);
  const turns = [];
  let current = null;

  for (const line of lines) {
    const m = line.match(SPEAKER_LABEL_RE);
    // Only treat as a labeled turn if the captured label looks plausible
    // (non-empty, < 40 chars — avoids matching arbitrary "Some sentence:"
    // mid-utterance lines).
    if (m && m[1] && m[1].length <= 40) {
      if (current) turns.push(current);
      current = { speaker: m[1].trim(), text: m[2] || "" };
    } else if (current) {
      const cont = line.trim();
      if (cont) {
        current.text = current.text ? `${current.text} ${cont}` : cont;
      }
    } else if (line.trim()) {
      // Leading content with no preceding label — render as unlabeled turn.
      current = { speaker: null, text: line.trim() };
    }
  }
  if (current) turns.push(current);

  // Strip enclosing quotes per turn.
  for (const t of turns) {
    t.text = t.text.replace(/^"+/, "").replace(/"+\s*$/, "").trim();
  }

  if (turns.length === 0) {
    return [
      <TurnBlock key={0} speaker={null} text={text} registry={registry} />,
    ];
  }

  return turns.map((t, i) => (
    <TurnBlock key={i} speaker={t.speaker} text={t.text} registry={registry} />
  ));
}

// RAW shape: array of word objects with `{ word, speaker_id, ... }`. The
// tagger emits this as a JSON-encoded string in `data.text`. Group
// consecutive words by `speaker_id` into a single turn.
function renderRawWords(words, registry) {
  if (!Array.isArray(words) || words.length === 0) return null;
  const turns = [];
  let current = null;
  for (const w of words) {
    const sp = w?.speaker_id ?? w?.speaker ?? null;
    const word = w?.word ?? w?.text ?? "";
    if (!current || current.speaker !== sp) {
      current = { speaker: sp, words: [word] };
      turns.push(current);
    } else {
      current.words.push(word);
    }
  }
  return turns.map((t, i) => (
    <TurnBlock
      key={i}
      speaker={t.speaker != null ? String(t.speaker) : null}
      text={t.words.join(" ").trim()}
      registry={registry}
    />
  ));
}

// Pick the renderer for a single detail item.
function renderDetailItem(item, registry) {
  const data = item?.data || {};
  const text = data.text ?? null;

  // RAW shape: try to parse as JSON array of word objects first.
  if (typeof text === "string") {
    const trimmed = text.trim();
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return renderRawWords(parsed, registry);
      } catch (e) {
        // fall through to formatted-text path
      }
    }
    return renderFormattedText(text, registry);
  }

  // Some backends stash the array directly on `data.words` or on `data`.
  if (Array.isArray(data.words)) return renderRawWords(data.words, registry);
  if (Array.isArray(data)) return renderRawWords(data, registry);

  // Fallback: stringify whatever we got so it's at least visible.
  return (
    <TurnBlock
      speaker={null}
      text={JSON.stringify(data)}
      registry={registry}
    />
  );
}

// --- Transcript tab body -----------------------------------------------------

const TranscriptTab = ({ items, loading, error, onRetry }) => {
  // Fresh per-render registry so colors are stable within a single tab but
  // independent across tabs.
  const registry = new Map();

  if (loading) {
    return (
      <Flex justify="center" py={10}>
        <Spinner size="md" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md" data-testid="transcript-error">
        <AlertIcon />
        <Box flex={1}>
          <AlertTitle fontSize="sm">Could not load transcript</AlertTitle>
          <AlertDescription fontSize="xs">{error}</AlertDescription>
        </Box>
        <Button size="xs" onClick={onRetry}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!items || items.length === 0) {
    return (
      <Box
        py={10}
        textAlign="center"
        bg="gray.50"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
        borderStyle="dashed"
        data-testid="transcript-empty"
      >
        <Text color="gray.500" fontSize="sm">
          Transcription still in progress.
        </Text>
      </Box>
    );
  }

  const sorted = [...items].sort(
    (a, b) => (a?.index ?? 0) - (b?.index ?? 0)
  );

  return (
    <VStack align="stretch" spacing={4} data-testid="transcript-list">
      {sorted.map((item, idx) => (
        <Box key={item.id ?? idx}>{renderDetailItem(item, registry)}</Box>
      ))}
    </VStack>
  );
};

// --- Metadata panel ----------------------------------------------------------

const MetadataField = ({ label, value }) => (
  <Box>
    <Text
      fontSize="xs"
      color="gray.500"
      textTransform="uppercase"
      letterSpacing="wider"
      fontWeight="semibold"
      mb={1}
    >
      {label}
    </Text>
    <Text fontSize="sm">{value ?? "—"}</Text>
  </Box>
);

const MetadataSection = ({ conversation }) => (
  <Box
    p={4}
    bg="white"
    borderRadius="lg"
    borderWidth="1px"
    borderColor="gray.200"
    data-testid="metadata-section"
  >
    <Heading size="sm" mb={3}>
      Recording details
    </Heading>
    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
      <MetadataField
        label="ID"
        value={
          <Text as="span" fontFamily="mono" fontSize="xs" color="gray.500">
            {conversation.id}
          </Text>
        }
      />
      <MetadataField
        label="Created"
        value={conversation.created_at ? formatDate(conversation.created_at) : null}
      />
      <MetadataField
        label="Ended"
        value={conversation.ended_at ? formatDate(conversation.ended_at) : null}
      />
      <MetadataField
        label="Recording"
        value={
          conversation.recording_state ? (
            <Badge colorScheme="blue" variant="subtle" fontSize="xs">
              {conversation.recording_state}
            </Badge>
          ) : null
        }
      />
      <MetadataField
        label="Pipeline"
        value={
          conversation.pipeline_state ? (
            <Badge colorScheme="purple" variant="subtle" fontSize="xs">
              {conversation.pipeline_state}
            </Badge>
          ) : null
        }
      />
      {conversation.pipeline_error_stage && (
        <MetadataField
          label="Pipeline error"
          value={
            <Badge colorScheme="red" variant="subtle" fontSize="xs">
              {conversation.pipeline_error_stage}
            </Badge>
          }
        />
      )}
    </SimpleGrid>
  </Box>
);

// --- Page shell --------------------------------------------------------------

// Tab type ordering matches PRD discussion: Cleaned is the default consumer
// view, Tagged is the diarization output, Raw is the per-word output.
const TAB_TYPES = ["CLEANED_TRANSCRIPT", "TAGGED_TRANSCRIPT", "RAW_TRANSCRIPT"];
const TAB_LABELS = {
  CLEANED_TRANSCRIPT: "Cleaned",
  TAGGED_TRANSCRIPT: "Tagged",
  RAW_TRANSCRIPT: "Raw",
};

const ConversationDetailPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();

  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track B PR3 — latest analysis run for the parent thread. Drives the
  // lifecycle bar's ANALYZING / ANALYZED stages. Best-effort: a missing
  // run just means the bar stops at ENDED.
  const [latestAnalysisRun, setLatestAnalysisRun] = useState(null);

  // BatchProgressTable's reducer is encapsulated; we drive it via a ref so
  // SSE events arriving at this page level dispatch directly into the table.
  const batchApplyRef = useRef(null);

  // Hoisted audio-player state so the player can drive the transcript
  // views (word highlight + auto-scroll) and the transcript views can
  // drive the player (click-to-seek). `audioSeekRef` exposes the
  // player's imperative seek API; see ConversationAudioPlayer.
  const [currentAudioMs, setCurrentAudioMs] = useState(0);
  // `audio_start_epoch_ms` from the audio manifest. Word/turn timestamps
  // in TAGGED `data.turns` + CLEANED `turns_meta` are EPOCH ms inherited
  // from the RAW stream — the player's currentTimeMs is conv-relative.
  // The views subtract this to bring everything into the same frame.
  const [audioStartEpochMs, setAudioStartEpochMs] = useState(0);
  const handleAudioManifest = useCallback((manifest) => {
    const v = Number(manifest?.audio_start_epoch_ms);
    if (Number.isFinite(v) && v >= 0) setAudioStartEpochMs(v);
  }, []);
  const audioSeekRef = useRef(null);
  const handleSeekToMs = useCallback((ms) => {
    audioSeekRef.current?.seek?.(ms);
  }, []);

  // Per-tab fetch state — keyed by detail-item type.
  const [tabState, setTabState] = useState(() => {
    const init = {};
    for (const t of TAB_TYPES) {
      init[t] = { items: null, loading: false, error: null };
    }
    return init;
  });

  // Ref to the inner scroll container (TabPanels). Passed down to the
  // structured views so `scrollIntoView` targets THIS box and not the
  // window — the page is laid out as a flex column with a sticky top
  // (header + tabs) and a sticky-bottom player.
  const scrollContainerRef = useRef(null);

  const fetchConversation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getConversation(conversationId);
      setConversation(resp || null);
    } catch (e) {
      const message =
        e?.response?.data?.detail || e?.message || "Failed to load conversation.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  const fetchTab = useCallback(
    async (type) => {
      setTabState((prev) => ({
        ...prev,
        [type]: { ...prev[type], loading: true, error: null },
      }));
      try {
        const resp = await getConversationDetailItems(conversationId, { type });
        const items = resp?.items || (Array.isArray(resp) ? resp : []) || [];
        setTabState((prev) => ({
          ...prev,
          [type]: { items, loading: false, error: null },
        }));
      } catch (e) {
        const message =
          e?.response?.data?.detail || e?.message || "Failed to load transcript.";
        setTabState((prev) => ({
          ...prev,
          [type]: { items: null, loading: false, error: message },
        }));
      }
    },
    [conversationId]
  );

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  // Eagerly load the default (Cleaned) tab AND raw — raw drives the
  // snippet-boundary overlay on the waveform regardless of which tab
  // the user is looking at. TAGGED still loads lazily on first tab
  // activation to keep the initial payload modest.
  useEffect(() => {
    if (!conversationId) return;
    fetchTab("CLEANED_TRANSCRIPT");
    fetchTab("RAW_TRANSCRIPT");
  }, [conversationId, fetchTab]);

  // Once we know the parent thread, fetch the latest analysis run to drive
  // the lifecycle bar's ANALYZING / ANALYZED stages.
  const threadIdState = conversation?.thread_id ?? null;
  const refetchLatestRun = useCallback(async () => {
    if (threadIdState == null) return;
    try {
      const resp = await listAnalysisRuns(threadIdState, { limit: 1 });
      const items = resp?.items || (Array.isArray(resp) ? resp : []) || [];
      setLatestAnalysisRun(items[0] || null);
    } catch (_e) {
      // Swallow — the bar simply stops at the last known stage.
    }
  }, [threadIdState]);
  useEffect(() => {
    if (threadIdState == null) return;
    refetchLatestRun();
  }, [threadIdState, refetchLatestRun]);

  // SSE — subscribe to the parent thread's event stream and feed
  // per-batch events into the BatchProgressTable, plus refresh the
  // analysis run on analysis_* events.
  useEffect(() => {
    if (threadIdState == null) return undefined;
    const token = currentEmbedToken?.() || "";
    const url = buildThreadEventsUrl(threadIdState, token);
    let es;
    try {
      es = defaultEventSourceFactory(url);
    } catch (_e) {
      return undefined;
    }
    if (!es) return undefined;

    const convIdNum = conversationId != null ? Number(conversationId) : null;

    const onEvent = (rawEvent) => {
      // EventSource delivers a MessageEvent; the JSON payload is in `.data`.
      let envelope = null;
      try {
        envelope =
          typeof rawEvent?.data === "string"
            ? JSON.parse(rawEvent.data)
            : rawEvent;
      } catch (_e) {
        return;
      }
      if (!envelope) return;

      // Per-batch table cares about events scoped to THIS conversation only.
      const eventConv = envelope.conversation_id;
      if (
        envelope.type &&
        envelope.type.startsWith("batch_") &&
        convIdNum != null &&
        eventConv != null &&
        eventConv !== convIdNum
      ) {
        return;
      }
      if (
        envelope.type === "raw_transcript_emitted" ||
        envelope.type === "tagged_transcript_ready" ||
        envelope.type === "cleaned_transcript_ready"
      ) {
        if (
          convIdNum != null &&
          eventConv != null &&
          eventConv !== convIdNum
        ) {
          return;
        }
      }

      if (typeof batchApplyRef.current === "function") {
        batchApplyRef.current(envelope);
      }

      if (
        envelope.type === "analysis_started" ||
        envelope.type === "analysis_starting" ||
        envelope.type === "analysis_complete" ||
        envelope.type === "analysis_failed"
      ) {
        // Re-fetch the latest analysis run so the lifecycle bar flips
        // through ANALYZING → ANALYZED (or back to ENDED on failure).
        refetchLatestRun();
      }
      if (envelope.type === "recording_state_changed") {
        // Re-fetch conversation metadata so recording_state updates the bar.
        fetchConversation();
      }
    };

    if (typeof es.addEventListener === "function") {
      es.addEventListener("message", onEvent);
      CONVERSATION_DETAIL_SSE_EVENTS.forEach((type) =>
        es.addEventListener(type, onEvent),
      );
    } else {
      es.onmessage = onEvent;
    }

    return () => {
      try {
        es.close();
      } catch (_e) {
        // ignore
      }
    };
  }, [threadIdState, conversationId, refetchLatestRun, fetchConversation]);

  const handleTabChange = (idx) => {
    const type = TAB_TYPES[idx];
    const state = tabState[type];
    if (state.items == null && !state.loading) {
      fetchTab(type);
    }
  };

  const threadId = conversation?.thread_id ?? null;

  return (
    <PageLayoutWithHeaderNav>
      <VStack
        spacing={6}
        align="stretch"
        data-testid="conversation-detail-page"
      >
        {/* Back link + title */}
        <Flex align="center" gap={2}>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<ChevronLeftIcon />}
            onClick={() =>
              threadId != null
                ? navigate(`/threads/${threadId}`)
                : navigate("/threads")
            }
            data-testid="conversation-back-button"
          >
            {threadId != null ? "Thread" : "Threads"}
          </Button>
          <HStack spacing={3}>
            <Heading size="lg">Recording</Heading>
            {conversation && (
              <Text
                as="span"
                fontFamily="mono"
                fontSize="sm"
                color="gray.400"
              >
                #{conversation.id}
              </Text>
            )}
          </HStack>
        </Flex>

        {loading && (
          <Flex justify="center" py={16}>
            <Spinner size="lg" />
          </Flex>
        )}

        {!loading && error && (
          <Alert
            status="error"
            borderRadius="md"
            data-testid="conversation-error"
          >
            <AlertIcon />
            <Box flex={1}>
              <AlertTitle fontSize="sm">Could not load recording</AlertTitle>
              <AlertDescription fontSize="xs">{error}</AlertDescription>
            </Box>
            <Button size="xs" onClick={fetchConversation}>
              Retry
            </Button>
          </Alert>
        )}

        {!loading && !error && conversation && (
          <>
            {/* Track B PR3 — lifecycle bar + per-batch progress table.
                Rendered above the transcript tabs per docs/plan-thread-ui.md.
            */}
            <Box data-testid="conversation-lifecycle-section">
              <ConversationLifecycleBar
                size="md"
                recordingState={conversation.recording_state}
                latestAnalysisRunStatus={latestAnalysisRun?.status}
                analysisCommitted={
                  latestAnalysisRun?.committed_at != null ||
                  (latestAnalysisRun?.status || "").toLowerCase() === "committed"
                }
              />
            </Box>

            <BatchProgressTable conversationId={conversationId} />

            <MetadataSection conversation={conversation} />

            <Box
              data-testid="transcript-section"
              display="flex"
              flexDirection="column"
              flex={1}
              minH={0}
            >
              <Heading size="md" mb={3}>
                Transcript
              </Heading>
              <Tabs
                variant="enclosed"
                colorScheme="blue"
                onChange={handleTabChange}
                isLazy
                display="flex"
                flexDirection="column"
                flex={1}
                minH={0}
              >
                {/* Tabs nav sticks to the top of the transcript region
                    so it remains visible while the panels below it
                    scroll independently. */}
                <TabList
                  position="sticky"
                  top={0}
                  zIndex={4}
                  bg="gray.50"
                  data-testid="transcript-tablist"
                >
                  {TAB_TYPES.map((t) => (
                    <Tab key={t} data-testid={`transcript-tab-${t}`}>
                      {TAB_LABELS[t]}
                    </Tab>
                  ))}
                </TabList>
                <TabPanels
                  ref={scrollContainerRef}
                  flex={1}
                  minH={0}
                  overflowY="auto"
                  // Cap height so the player can't be pushed off-screen
                  // by a tall transcript — the panels become the scroll
                  // container, not the window.
                  maxH="calc(100vh - 320px)"
                  data-testid="transcript-panels"
                >
                  {TAB_TYPES.map((t) => {
                    const state = tabState[t];
                    // For TAGGED + CLEANED, route through the structured
                    // renderers when items carry `data.turns` /
                    // `data.turns_meta`. The renderers fall back to the
                    // legacy text path when the new fields are absent
                    // (older data not yet re-tagged).
                    const legacyRenderer = (sortedItems) => (
                      <TranscriptTab
                        items={sortedItems}
                        loading={state.loading}
                        error={state.error}
                        onRetry={() => fetchTab(t)}
                      />
                    );
                    let content;
                    if (t === "TAGGED_TRANSCRIPT" && state.items && !state.loading && !state.error) {
                      content = (
                        <TaggedTranscriptView
                          items={state.items}
                          currentTimeMs={currentAudioMs}
                          audioStartEpochMs={audioStartEpochMs}
                          onSeekToMs={handleSeekToMs}
                          legacyRenderer={legacyRenderer}
                        />
                      );
                    } else if (
                      t === "CLEANED_TRANSCRIPT" &&
                      state.items &&
                      !state.loading &&
                      !state.error
                    ) {
                      content = (
                        <CleanedTranscriptView
                          items={state.items}
                          currentTimeMs={currentAudioMs}
                          audioStartEpochMs={audioStartEpochMs}
                          onSeekToMs={handleSeekToMs}
                          legacyRenderer={legacyRenderer}
                        />
                      );
                    } else if (
                      t === "RAW_TRANSCRIPT" &&
                      state.items &&
                      !state.loading &&
                      !state.error
                    ) {
                      content = (
                        <RawTranscriptView
                          items={state.items}
                          currentTimeMs={currentAudioMs}
                          audioStartEpochMs={audioStartEpochMs}
                          onSeekToMs={handleSeekToMs}
                          legacyRenderer={legacyRenderer}
                        />
                      );
                    } else {
                      content = (
                        <TranscriptTab
                          items={state.items}
                          loading={state.loading}
                          error={state.error}
                          onRetry={() => fetchTab(t)}
                        />
                      );
                    }
                    return (
                      <TabPanel
                        key={t}
                        px={0}
                        data-testid={`transcript-panel-${t}`}
                      >
                        {content}
                      </TabPanel>
                    );
                  })}
                </TabPanels>
              </Tabs>
            </Box>
          </>
        )}
      </VStack>

      {/* Sticky-bottom audio player. Rendered ONLY when the conversation
          has finished loading. The Prev/Next-turn jump targets are
          synthesized from TAGGED `data.turns` if present (most current),
          else from CLEANED `data.turns_meta`. When neither is present
          (legacy data) the jump buttons are disabled and the player
          still serves as a simple play/seek surface. */}
      {!loading && !error && conversation && (
        <ConversationAudioPlayer
          conversationId={conversationId}
          snippetBoundaries={(() => {
            // Snippet regions on the waveform are sourced from the RAW
            // word stream (every word carries `source_snippet_id`). When
            // RAW hasn't loaded yet the player draws no regions; once
            // RAW arrives, the player re-paints with the new boundary
            // set. See `collectSnippetBoundaries` in RawTranscriptView.
            const rawItems = tabState?.RAW_TRANSCRIPT?.items || [];
            return collectSnippetBoundaries(rawItems, audioStartEpochMs);
          })()}
          turnStartsMs={(() => {
            // Convert epoch ms → conv-relative ms by subtracting
            // audioStartEpochMs. The player's Prev/Next-turn seek
            // targets the WaveSurfer position (0..duration), not epoch.
            const normalize = (vals) =>
              vals
                .filter((v) => Number.isFinite(v))
                .map((v) => Math.max(0, v - audioStartEpochMs))
                .sort((a, b) => a - b);
            const tagged = tabState?.TAGGED_TRANSCRIPT?.items || [];
            for (const item of tagged) {
              const turns = item?.data?.turns;
              if (Array.isArray(turns) && turns.length > 0) {
                return normalize(turns.map((t) => Number(t?.start_ms)));
              }
            }
            const cleaned = tabState?.CLEANED_TRANSCRIPT?.items || [];
            for (const item of cleaned) {
              const meta = item?.data?.turns_meta;
              if (Array.isArray(meta) && meta.length > 0) {
                return normalize(meta.map((m) => Number(m?.start_ms)));
              }
            }
            return [];
          })()}
          onTimeChange={setCurrentAudioMs}
          onManifestLoaded={handleAudioManifest}
          seekRef={audioSeekRef}
        />
      )}
    </PageLayoutWithHeaderNav>
  );
};

export default ConversationDetailPage;
